import { printError, useUtilities } from "helpers";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  defaultTaskAdd,
  defaultTaskRemove,
  defaultTaskShow,
  templateTaskAdd,
  templateTaskDelete,
  templateTaskEdit,
  templateTaskShow,
} from "./modularApi";
import { ApiResponseType, NotificationType } from "enums";
import {
  addTemplateNote,
  deleteTemplateNote,
  getAllTemplateNotes,
  updateTemplateNote,
  addDefaultSiteNote,
  removeDefaultSiteNote,
  getDefaultSiteNotes,
} from "redux/reducers/templateLibrary/notes";
import { useEffect } from "react";
import { t } from "i18next";

export function useAllNotes() {
  return useQuery(["getallnotes"], () => templateTaskShow("notes"));
}

export function useTemplateNotes() {
  const { data, isLoading, error } = useAllNotes();
  const {
    dispatch,
    notes: { notes, loaded },
  } = useUtilities();
  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) {
      dispatch(getAllTemplateNotes(data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded]);
  return { data: notes, isLoading: isLoading || !loaded, error };
}

export function useAddNoteTemplate(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskAdd({
        ...params,
        parent_type: "notes",
        module_hint: "notes",
      }),
    {
      onSuccess: (data) => {
        if (data.status_code === ApiResponseType.SUCCESS) {
          dispatch(addTemplateNote(data.data));
          notification({
            message: t("libraryTemplate.notes.api.success.added"),
            type: NotificationType.SUCCESS,
          });
          if (redirect) navigate(redirect + data.data.id);
        } else {
          notification({
            message: printError(data.message),
            type: NotificationType.ERROR,
          });
        }
      },
      onError: (error) => {
        notification({
          message: printError(error.message),
          type: NotificationType.ERROR,
        });
      },
    }
  );
}

export function useUpdateNoteTemplate() {
  const { dispatch, notification } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskEdit({
        ...params,
        parent_type: "notes",
        module_hint: "notes",
      }),
    {
      onSuccess: (data) => {
        if (data.data.id) {
          dispatch(updateTemplateNote(data.data));
          notification({
            message: t("libraryTemplate.notes.api.success.updated"),
            type: "success",
          });
        } else {
          notification({
            message: printError(data.message),
            type: "error",
          });
        }
      },
      onError: (error) => {
        notification({
          message: printError(error.message),
          type: "error",
        });
      },
    }
  );
}

export function useDeleteNoteTemplate(redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(templateTaskDelete, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(deleteTemplateNote({ id: data.id }));
        notification({
          message: t("libraryTemplate.notes.api.success.deleted"),
          type: "success",
        });
        if (redirect) navigate(redirect);
      } else {
        notification({
          message: printError(data.message),
          type: "error",
        });
      }
    },
    onError: (error) => {
      notification({
        message: printError(error.message),
        type: "error",
      });
    },
  });
}

export function useAllDefaultNote() {
  return useQuery(["getalldefaultnote"], () => defaultTaskShow("notes"));
}

export function useDefaultNotes() {
  const { data, isLoading, error } = useAllDefaultNote();
  const {
    dispatch,
    notes: {
      defaultSite: { notes, loaded },
    },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) {
      dispatch(getDefaultSiteNotes(data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded]);

  return { data: notes, isLoading: isLoading || !loaded, error };
}

export function useAddDefaultNote() {
  const { dispatch, notification } = useUtilities();
  return useMutation(defaultTaskAdd, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addDefaultSiteNote(data.data));
        notification({
          message: t("libraryTemplate.notes.api.success.addedToSiteTemplate"),
          type: "success",
        });
      } else {
        notification({
          message: printError(
            data.message ||
              t("libraryTemplate.notes.api.error.failedToAddInSiteTemplate")
          ),
          type: "error",
        });
      }
    },
    onError: (error) => {
      notification({
        message:
          error.message ||
          t("libraryTemplate.notes.api.error.failedToAddInSiteTemplate"),
        type: "error",
      });
    },
  });
}

export function useRemoveDefaultNote() {
  const { dispatch, notification } = useUtilities();
  return useMutation(defaultTaskRemove, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(removeDefaultSiteNote({ id: data.id }));
        notification({
          type: "success",
          message: t(
            "libraryTemplate.notes.api.success.removedFromSiteTemplate"
          ),
        });
      } else {
        notification({
          type: "error",
          message: printError(
            data.message ||
              t(
                "libraryTemplate.notes.api.error.failedToRemoveFromSiteTemplate"
              )
          ),
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: printError(
          error.message ||
            t("libraryTemplate.notes.api.error.failedToRemoveFromSiteTemplate")
        ),
      });
    },
  });
}
